import React, { useContext } from 'react';

import clsx from 'clsx';
import { Box, Chip, Link, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';

import { DeviceContext } from '../../../contexts';
import { AIChatContext } from '../context';
import { LailaOrb } from './launcher/LailaOrb';

// Only what Laila can do today: the employer side has clock-in and time-log tools,
// everyone can ask about their own pay.
export const EMPLOYER_SUGGESTIONS = [
  "Who's clocked in right now?",
  'Who has clocked out today?',
  'What do we need for CDM 2015 compliance?',
  'Help me plan a timeline for a residential build',
];
export const WORKER_SUGGESTIONS = [
  'When will I get paid?',
  'What does my CSCS card let me do?',
  'Help me prepare for a site induction',
  'What should I check before signing a subcontract?',
  'Explain CIS deductions in simple terms',
];

const greeting = (hour: number) => (hour < 12 ? 'Morning' : hour < 18 ? 'Afternoon' : 'Evening');

interface Props {
  onShowIdeas?: () => void;
  onShowTips?: () => void;
  // For the side panel: a smaller orb.
  compact?: boolean;
}

export const LailaWelcome: React.FC<Props> = ({ onShowIdeas, onShowTips, compact = false }) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const { user, send } = useContext(AIChatContext);
  const isEmployer = !!user?.positions?.length;
  const orbSize = compact ? 64 : isMobile ? 88 : 112;

  return (
    <Box className={classes.root}>
      <LailaOrb size={orbSize} className={clsx(classes.orb, { [classes.orbLarge]: !compact })} />

      <Typography variant="h5" className={classes.title}>
        {greeting(new Date().getHours())}
        {user?.first_name ? `, ${user.first_name}` : ''}
      </Typography>
      <Typography color="textSecondary" className={classes.subtitle}>
        I'm Laila, your AI assistant. Ask me about your work, or pick a starting point.
      </Typography>

      <Box className={classes.chips}>
        {(isEmployer ? EMPLOYER_SUGGESTIONS : WORKER_SUGGESTIONS).map(suggestion => (
          <Chip
            key={suggestion}
            label={suggestion}
            clickable
            onClick={() => send(suggestion)}
            className={classes.chip}
          />
        ))}
      </Box>

      {(onShowIdeas || onShowTips) && (
        <Box className={classes.links}>
          {onShowIdeas && (
            <Link component="button" variant="body2" onClick={onShowIdeas}>
              More ideas
            </Link>
          )}
          {onShowTips && (
            <Link component="button" variant="body2" onClick={onShowTips}>
              Tips for better answers
            </Link>
          )}
        </Box>
      )}

      <Typography variant="caption" color="textSecondary" className={classes.note}>
        Laila only sees what you can see in Vyce.
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      maxWidth: 560,
      margin: '0 auto',
      padding: '24px 16px',
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    orb: { margin: '20px 0 12px' },
    // Room for the halo, which reaches well past the orb.
    orbLarge: { margin: '44px 0 28px' },
    title: { marginTop: 8, fontWeight: 600 },
    subtitle: { marginTop: 6, maxWidth: 420 },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 8,
      marginTop: 24,
    },
    chip: {
      height: 'auto',
      padding: '8px 4px',
      borderRadius: 18,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      '& .MuiChip-label': { whiteSpace: 'normal' },
      '&:hover, &:focus': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      },
    },
    links: { display: 'flex', gap: 20, marginTop: 20 },
    note: { marginTop: 24 },
    '@keyframes enter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
  })
);
